/* eslint-disable no-param-reassign */

import * as ModelEvent from "@node-elion/syncron";
import { LatLngLiteral } from "leaflet";
import { cloneDeep, isBoolean } from "lodash";

import Map from "../../redux/services/Map";
import SubscriptionPool from "../../redux/services/SubscriptionPool";
import createLogger from "../../utils/logger.util";
import { createObjectLanguageNamesFromObject } from "../../assets/languages/langs";
import Subscription from "../../types/Subscription";
import { Company, Language, Base, CarClass, Service } from "..";

const logger = createLogger({ name: "TaxiService2" });

class TaxiService extends Base {
	static fromResponse(data: any): TaxiService.Model {
		return {
			id: data.id,

			company: data.company
				? Company.fromResponse(data.company)
				: undefined,
			settlement: createObjectLanguageNamesFromObject(data.settlement),

			coordinates: data.settlementPoint,
			settings: cloneDeep(data.settings),

			createdAt: data.createdAt,
			updatedAt: data.updatedAt,
			deletedAt: data.deletedAt,
		};
	}

	static toRequest(model: TaxiService.Model.Modified): any {
		const payload: TaxiService.Model.Modified = {
			settings: model.settings,
		};

		if (
			isBoolean(model.settings?.sectors?.isBasicAverageSpeedEnabled) &&
			payload.settings
		) {
			payload.settings.sectors.additionalFields.isBasicAverageSpeedEnabled =
				model?.settings?.sectors?.additionalFields
					?.isBasicAverageSpeedEnabled || false;
		}

		return payload;
	}

	static async update(object: TaxiService.Model.Modified) {
		this.request((prpc) =>
			prpc.theirsModel.taxiService.update(
				object.id,
				TaxiService.toRequest(object),
			),
		);
	}

	public static async update2(
		object: TaxiService.Model.Modified,
	): Promise<boolean> {
		try {
			const res = await this.request(
				(prpc) =>
					prpc.theirsModel.taxiService.update(
						object.id,
						TaxiService.toRequest(object),
					),
				{ silent: false, error: true },
			);
			logger.info("[TaxiService] update2", { object, res });
			if (res.error) throw res;
			return res;
		} catch (err: any) {
			logger.error("[TaxiService] Error update2:", err);
			return false;
		}
	}

	public static async subscribe(
		options: TaxiService.SubscribeOptions,
		onUpdate: Subscription.OnUpdate<TaxiService.Model>,
	): Promise<Subscription<TaxiService.SubscribeOptions> | null> {
		const modelEventConstructor = new ModelEvent.ModelEventConstructor({
			onUpdate: (state) => {
				logger.info("[TaxiService] subscribe", { state, options });
				onUpdate({
					...state,

					models: state.models.map(this.fromResponse),
				});
			},
		});

		const subscription = await SubscriptionPool.add(
			(prpc) =>
				prpc.theirsModel.taxiService.subscribe({
					params: this.optionsToRequest(options),
					ping: () => true,
					onEvent: (events) => {
						modelEventConstructor.onEvent(events);
					},
					onError: (error) => {
						logger.error("[TaxiService] subscribe error", options);
						logger.error(error);
					},
				}),
			{ name: "TaxiService.subscribe" },
		);

		return {
			unsubscribe: () => subscription.unsubscribe(),
			update: (options: TaxiService.SubscribeOptions) =>
				subscription.update(this.optionsToRequest(options)),
		} as Subscription<TaxiService.SubscribeOptions>;
	}

	public static async getById(id: number): Promise<TaxiService.Model | null> {
		const titleError = "[TaxiService] Error getById:";
		try {
			const result = await this.request(
				(prpc) => prpc.theirsModel.taxiService.getById(id),
				{ silent: false, error: true },
			);

			if (result && result.error) {
				logger.error(titleError, result.data);
				return null;
			}

			return result;
		} catch (err: any) {
			logger.error(titleError, err);
			return null;
		}
	}

	public static async getAllTransfer(
		params: {
			order?: Record<string, "ASC" | "DESC">;
			query?: {
				taxiServiceId?: number;
			};
			limit?: number;
			offset?: number;
		} = {},
	): Promise<TaxiService.TransferModel[] | null> {
		try {
			const res = await this.request(
				(prpc) =>
					prpc.theirsModel.settings.order.orderTransfer.getAll(
						params,
					),
				{ silent: false, error: true },
			);

			logger.info("[TaxiService] getAllTransfer", {
				params,
				res,
			});

			return res?.items || [];
		} catch (err: any) {
			logger.error("[TaxiService] Error getAllTransfer:", err);
			return null;
		}
	}

	public static async patchTransfer(
		transferId: number,
		object: TaxiService.TransferUpdate,
	): Promise<TaxiService.SuperTransferSettings["send"][number] | null> {
		try {
			logger.info("[TaxiService] patchTransfer: object", object);

			const response = await this.request(
				(prpc) =>
					prpc.theirsModel.settings.order.orderTransfer.update(
						transferId,
						object,
					),
				{ silent: false, error: true },
			);
			logger.info("[TaxiService] patchTransfer: response", response);
			return response;
		} catch (err: any) {
			logger.error("[TaxiService] Error patchTransfer:", err);
			return null;
		}
	}

	private static optionsToRequest(options: TaxiService.SubscribeOptions) {
		return {
			ids: options.ids,
			companyIds: options.companyIds,

			query: options.query,
			limit: options.limit,
			offset: options.offset,
			lang: options.language,
			terse: true,
			near: options.near,
		};
	}
}

/**
 * @enum TransferType
 *
 * Represents all possible **transfer combinations** between services (excluding "ALL").
 *
 * This enum is used to define the allowed types of order transfers between services.
 *
 * Rules:
 * 1. "ALL" is not included inside combinations, as it represents "all services".
 * 2. Combinations include 2 or 3 different services.
 *
 * Представляє всі можливі **комбінації перекидів замовлень між службами** (без "ALL").
 *
 * Цей enum використовується для визначення допустимих типів перекиду замовлень між службами.
 *
 * Правила:
 * 1. "ALL" не включається всередину комбінацій, оскільки він означає "всі служби".
 * 2. Комбінації містять 2 або 3 різні служби.
 *
 */
export enum TransferType {
	NOT = "not",
	ALL = "all",
	LOCAL = "local",
	GLOBAL = "global",
	OUTSIDE = "outside",
	EVOS = "evos",
}

/**
 * @enum TransferTypeConst
 *
 * Represents all possible **transfer combinations** between services (excluding "ALL").
 *
 * This enum is used to define the allowed types of order transfers between services.
 *
 * Rules:
 * 1. "ALL" is not included inside combinations, as it represents "all services".
 * 2. Combinations include 2 or 3 different services.
 *
 * Представляє всі можливі **комбінації перекидів замовлень між службами** (без "ALL").
 *
 * Цей enum використовується для визначення допустимих типів перекиду замовлень між службами.
 *
 * Правила:
 * 1. "ALL" не включається всередину комбінацій, оскільки він означає "всі служби".
 * 2. Комбінації містять 2 або 3 різні служби.
 *
 */
export const TransferTypeConst = {
	NOT: "not",
	ALL: "all",
	LOCAL: "local",
	GLOBAL: "global",
	OUTSIDE: "outside",
	EVOS: "evos",
} as const;
export type TypeTransferType = typeof TransferTypeConst;
export type ValueTransferType =
	(typeof TransferTypeConst)[keyof typeof TransferTypeConst];

/**
 * @enum TransferMode
 *
 * Represents the **mode of operation** for a service in order transfers.
 *
 * Possible values:
 * - SEND: The service only **sends** orders to other services.
 * - RECEIVE: The service only **receives** orders from other services.
 * - BOTH: The service can **both send and receive** orders.
 * - UNACTIVE: (optional) The service **does not participate** in sending or receiving.
 *
 * Rules:
 * - The mode cannot be empty or any value outside this enum.
 * - Use this enum to clearly define the behavior of a service in the transfer process.
 *
 * Example usage:
 *   const mode: TransferMode = TransferMode.SEND;
 *
 *  * Відображає **режим роботи служби** при перекиді замовлень.
 *
 * Можливі значення:
 * - SEND: Служба тільки **відправляє** замовлення іншим службам.
 * - RECEIVE: Служба тільки **приймає** замовлення від інших служб.
 * - BOTH: Служба може **і відправляти, і приймати** замовлення.
 * - UNACTIVE: (опціонально) Служба **не бере участь** в відправці або прийомі замовлень.
 *
 * Правила:
 * - Значення не може бути порожнім або відмінним від цього enum.
 * - Використовуйте цей enum для чіткого визначення поведінки служби у процесі перекиду.
 *
 * Приклад використання:
 *   const mode: TransferMode = TransferMode.SEND;
 */
export enum TransferMode {
	/**
	 * * The service only **sends** orders to other services
	 * * Служба тільки **відправляє** замовлення іншим службам
	 */
	SEND = "send",
	/**
	 * * The service only **receives** orders from other services
	 * * Служба тільки **приймає** замовлення від інших служб.
	 */
	RECEIVE = "receive",
	/**
	 * * The service can **both send and receive** orders
	 * * Служба може **і відправляти, і приймати** замовлення.
	 */
	BOTH = "both",
	/**
	 * * The service **does not participate** in sending or receiving
	 * * Служба **не бере участь** в відправці або прийомі замовлень.
	 * */
	UNACTIVE = "unactive",
}

export const TransferModeConst = {
	/**
	 * * The service only **sends** orders to other services
	 * * Служба тільки **відправляє** замовлення іншим службам
	 */
	SEND: "send",
	/**
	 * * The service only **receives** orders from other services
	 * * Служба тільки **приймає** замовлення від інших служб.
	 */
	RECEIVE: "receive",
	/**
	 * * The service can **both send and receive** orders
	 * * Служба може **і відправляти, і приймати** замовлення.
	 */
	BOTH: "both",
	/**
	 * * The service **does not participate** in sending or receiving
	 * * Служба **не бере участь** в відправці або прийомі замовлень.
	 * */
	UNACTIVE: "unactive",
} as const;
export type TypeTransferMode = typeof TransferModeConst;
export type ValueTransferMode =
	(typeof TransferModeConst)[keyof typeof TransferModeConst];

export enum TransferPayment {
	CARD = "card",
	CASH = "cash",
	INVOICE = "invoice",
	COMPANY_BILL = "company_bill",
	AGENT = "agent",
	PRELIMINARY = "preliminary",
	BONUS = "bonus",
}

export const TransferPaymentConst = {
	CARD: "card",
	CASH: "cash",
	INVOICE: "invoice",
	COMPANY_BILL: "company_bill",
	AGENT: "agent",
	PRELIMINARY: "preliminary",
	BONUS: "bonus",
} as const;
export type TypeTransferPayment = typeof TransferPaymentConst;
export type ValueTransferPayment =
	(typeof TransferPaymentConst)[keyof typeof TransferPaymentConst];

export enum ItemActionType {
	ADD = "add",
	REMOVE = "remove",
	UPDATE = "update",
	RESTORE = "restore",
}

export const ItemActionTypeConst = {
	ADD: "add",
	REMOVE: "remove",
	UPDATE: "update",
	RESTORE: "restore",
} as const;
export type TypeItemActionType = typeof ItemActionTypeConst;
export type ValueItemActionType =
	(typeof ItemActionTypeConst)[keyof typeof ItemActionTypeConst];

declare namespace TaxiService {
	interface Model {
		id: number;
		additionalFields?: Record<string, any> & {
			isBasicAverageSpeedEnabled?: boolean;
		};
		company?: Company.Model;

		settlement: Record<Map.Language, string>;
		coordinates: LatLngLiteral;
		settings: Record<string, any>;

		createdAt: string | null;
		updatedAt: string | null;
		deletedAt: string | null;
	}

	interface SubscribeOptions {
		ids?: number[];
		companyIds?: number[];

		query?: string;
		limit?: number;
		offset?: number;
		language?: Language;
		terse?: boolean;
		near?: LatLngLiteral;
	}

	interface ObjectLiteral {
		[key: string]: any;
	}

	interface ExternalCarClass {
		id: number;
		name: ObjectLiteral;
	}

	interface ExternalService {
		id: number;
		name: ObjectLiteral;
	}

	interface ExternalCompany {
		id: number;
		name: ObjectLiteral;
	}

	interface ExternalTaxiServiceName {
		company: ObjectLiteral;
		settlement: ObjectLiteral;
		isEnabled: boolean;
	}

	interface ExternalTaxiService {
		pointer: string;
		canonicalName: ExternalTaxiServiceName;
		/**
		 * @description Additional fields for transfer (receive)
		 * */
		availableCarClasses?: ExternalCarClass[];
		/**
		 * @description Additional fields for transfer (receive)
		 * */
		availableServices?: ExternalService[];
		isEnabled: any;
	}

	interface TransferModel {
		id: string;

		data: SuperTransferSettings;
		taxiService: {
			id: number;
			globalPointer: string;
			accountCode: string;
			settlement: ObjectLiteral;

			company: ExternalCompany;
			taxiServiceToCarClasses: TaxiServiceToCarClasses[];
			taxiServiceToServices: TaxiServiceToService[];

			createdAt: string | null;
			updatedAt: string | null;
			deletedAt: string | null;
		};

		type: TransferMode;
		enabled: boolean;
	}

	export interface SuperService {
		id: number;
		mainId: number;
		name: ObjectLiteral;
	}

	export interface SuperCarClass {
		id: number;
		mainId: number;
		name: ObjectLiteral;
	}

	export interface SuperServices {
		id: number;
		active: boolean;
		service: SuperService;
	}

	export interface SuperCarClasses {
		id: number;
		active: boolean;
		carClass: SuperCarClass;
	}
	export interface SuperSubsidiaryFromTo {
		id: number;
		mainId: number;
		name: string;
		globalId: string;
		settlement?: ObjectLiteral;
		company?: ObjectLiteral;
		services: SuperServices[];
		carClasses: SuperCarClasses[];
	}

	export interface SuperServicesTransfer {
		id: number;
		active: boolean;
		mode: TransferMode;
		types: TransferType[];
		from: SuperService;
		to: SuperService;
	}

	export interface SuperCarClassesTransfer {
		id: number;
		active: boolean;
		mode: TransferMode;
		types: TransferType[];
		from: SuperCarClass;
		to: SuperCarClass;
	}

	export interface SuperTransferSettings {
		id: number;
		createdAt: Date;
		updatedAt: Date;
		deletedAt: Date;
		mainId: number;
		globalId: string;
		isCustom: boolean;
		active: boolean;
		name: string;
		comment: string;
		mode: TransferMode;
		types: TransferType[];

		services: SuperServices[];
		carClasses: SuperCarClasses[];

		send: {
			id: number;
			active: boolean;
			mode: TransferMode;
			types: TransferType[];
			payments: TransferPayment[];
			carClasses: SuperCarClassesTransfer[];
			services: SuperServicesTransfer[];
			to: SuperSubsidiaryFromTo;
		}[];

		receive: {
			id: number;
			active: boolean;
			mode: TransferMode;
			types: TransferType[];
			payments: TransferPayment[];
			from: Pick<
				SuperSubsidiaryFromTo,
				"id" | "globalId" | "mainId" | "name" | "company" | "settlement"
			>;
		}[];
	}

	interface TaxiServiceToCarClasses {
		carClass: CarClass.Model;
		deletedAt: string | null;
		id: number;
	}

	interface TaxiServiceToService {
		carClass: Service.Model;
		deletedAt: string | null;
		id: number;
	}

	interface TransferReceiveMutate {
		id: SuperTransferSettings["receive"][0]["id"];
		mode: SuperTransferSettings["receive"][0]["mode"];
		payments: SuperTransferSettings["receive"][0]["payments"];
		types: SuperTransferSettings["receive"][0]["types"];
		active: SuperTransferSettings["receive"][0]["active"];
		data: SuperTransferSettings["receive"][0]["from"];
	}

	interface TransferSenderMutate {
		id: SuperTransferSettings["id"];
		sendId: SuperTransferSettings["send"][0]["id"];

		mode: SuperTransferSettings["mode"];
		types: SuperTransferSettings["types"];

		active: SuperTransferSettings["active"];
		isCustom: SuperTransferSettings["isCustom"];

		createdAt: SuperTransferSettings["createdAt"];
		updatedAt: SuperTransferSettings["updatedAt"];
		deletedAt: SuperTransferSettings["deletedAt"];

		mainId: SuperTransferSettings["mainId"];
		globalId: SuperTransferSettings["globalId"];

		comment: SuperTransferSettings["comment"];

		name: SuperTransferSettings["name"];
		settlement?: ObjectLiteral;
		company?: ObjectLiteral;
	}

	interface TransferModelMutate {
		id: SuperTransferSettings["send"][0]["id"];
		active: SuperTransferSettings["send"][0]["active"];
		mode: SuperTransferSettings["send"][0]["mode"];
		types: SuperTransferSettings["send"][0]["types"];
		payments: SuperTransferSettings["send"][0]["payments"];
		sender: TransferSenderMutate | null;
		receiver: TransferReceiveMutate | null;
		subsidiary: TransferModel;
		taxiService: TransferModel["taxiService"];
		senderCarClasses: SuperTransferSettings["carClasses"];
		senderServices: SuperTransferSettings["services"];
		receiveCarClasses: SuperTransferSettings["send"][0]["to"]["carClasses"];
		receiveServices: SuperTransferSettings["send"][0]["to"]["services"];
		transferServices: SuperTransferSettings["send"][0]["services"];
		transferCarClasses: SuperTransferSettings["send"][0]["carClasses"];
	}

	namespace OrderTransfer {
		export namespace AdditionalFields {
			export interface CarClassSync {
				originId: number | null; //* Our
				internalId: number | null; //* Compatible with
				indexMap?: number; //* only for transmutation update
			}

			export interface ServiceSync {
				originId: number | null; //* Our
				internalId: number | null; //* Compatible with
				indexMap?: number; //* only for transmutation update
			}
		}
		export interface AdditionalFields {
			carClasses: AdditionalFields.CarClassSync[];
			services: AdditionalFields.ServiceSync[];
		}
	}

	export type Keys = "carClassesTransfer" | "servicesTransfer" | "transfer";

	export interface DataGeneric<D> {
		/** If type is 'add', it can be omitted */
		id: number;
		type: ValueItemActionType;
		key: Keys;
		data: D;
	}

	interface Task {
		id: number;
		type: string;
		key: string;
		data: Record<string, any>;
	}

	interface TransferUpdate {
		tasks?: Task[];
	}

	namespace Model {
		type Modified = Partial<
			Omit<Model, "createdAt" | "updatedAt" | "deletedAt">
		>;
	}
}

export default TaxiService;
